import { useContext, useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import {
  CalendarClock,
  ExternalLink,
  FileText,
  Flame,
  Image as ImageIcon,
  Inbox,
  LayoutDashboard,
  LogOut,
  Medal,
  Megaphone,
  Newspaper,
  Server,
  ShieldAlert,
  Trophy,
  UserCog,
  Users,
} from 'lucide-react';
import { db } from '../../../firebase/config';
import { AuthContext } from '../../../contexts/AuthContext';
import { cn } from '../../../lib/utils';
import kzcompLogo from '../../../kzcomp.png';

const SidebarLink = ({ item, onNavigate }) => {
  const Icon = item.icon;
  const location = useLocation();
  const active = location.pathname === item.path;

  return (
    <NavLink
      to={item.path}
      onClick={onNavigate}
      title={item.description || undefined}
      className={cn(
        'group relative flex items-center justify-between rounded-md px-3 py-2 text-sm transition-colors',
        active
          ? 'bg-secondary text-foreground'
          : 'text-muted-foreground hover:bg-secondary/60 hover:text-foreground'
      )}
    >
      {active && <span className="absolute left-0 top-1/2 h-4 w-0.5 -translate-y-1/2 bg-heat" />}
      <span className="flex items-center gap-3">
        {Icon && <Icon className="h-4 w-4" />}
        <span className="truncate">{item.label}</span>
      </span>
      {item.badge ? (
        <span className="ml-2 inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-heat px-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-background">
          {item.badge}
        </span>
      ) : null}
    </NavLink>
  );
};

const Group = ({ heading, children }) => (
  <div className="mb-5">
    <div className="px-3 pb-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground/80">
      {heading}
    </div>
    <div className="flex flex-col gap-0.5">{children}</div>
  </div>
);

const AdminSidebar = ({ onNavigate }) => {
  const { currentUser, isSuperAdmin, hasPermission, logout } = useContext(AuthContext);
  const superAdmin = isSuperAdmin();
  const canManageTournaments = hasPermission('tournaments.manage');
  const canManageTeams = hasPermission('teams.manage');
  const canReviewSubmissions = hasPermission('mapSubmissions.review');
  const canManageMapImages = hasPermission('mapImages.manage');
  const canManageMedia = hasPermission('media.manage');
  const canManageMotw = hasPermission('mapOfTheWeek.manage');
  const canEditContent = hasPermission('content.edit');
  const canManageNews = hasPermission('news.manage');
  const canManageServers = hasPermission('servers.manage');
  const canManageStaff = hasPermission('staff.manage');
  const canModerateComments = hasPermission('profileComments.moderate');
  const canModerate = canManageNews || canModerateComments;

  const [pendingSubmissions, setPendingSubmissions] = useState(0);
  useEffect(() => {
    if (!currentUser) {
      setPendingSubmissions(0);
      return undefined;
    }
    const q = query(collection(db, 'mapSubmissions'), where('status', '==', 'pending'));
    const unsub = onSnapshot(
      q,
      (snap) => setPendingSubmissions(snap.size),
      () => setPendingSubmissions(0)
    );
    return unsub;
  }, [currentUser]);

  return (
    <aside className="flex h-full w-full flex-col gap-2 border-r border-border/60 bg-background/60 backdrop-blur">
      <div className="flex items-center justify-between px-5 pt-5">
        <Link to="/admin/dashboard" className="flex items-center gap-2" onClick={onNavigate}>
          <img src={kzcompLogo} alt="KZCOMP" className="h-7 w-auto" />
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
            admin
          </span>
        </Link>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 pb-4 pt-4">
        {/* GLOBAL ---------------------------------------------------------- */}
        <Group heading="Overview">
          <SidebarLink
            item={{ label: 'Dashboard', path: '/admin/dashboard', icon: LayoutDashboard }}
            onNavigate={onNavigate}
          />
        </Group>

        {(canManageTournaments || canManageTeams) && (
          <Group heading="Tournaments">
            {canManageTournaments && (
              <SidebarLink
                item={{ label: 'All tournaments', path: '/admin/tournaments', icon: Trophy }}
                onNavigate={onNavigate}
              />
            )}
            {canManageTournaments && (
              <SidebarLink
                item={{ label: 'Winners', path: '/admin/tournament-winners', icon: Medal }}
                onNavigate={onNavigate}
              />
            )}
            {canManageTournaments && (
              <SidebarLink
                item={{ label: 'Supporters', path: '/admin/supporters', icon: Flame }}
                onNavigate={onNavigate}
              />
            )}
            {canManageTeams && (
              <SidebarLink
                item={{ label: 'Teams', path: '/admin/teams', icon: Users }}
                onNavigate={onNavigate}
              />
            )}
          </Group>
        )}

        {(canManageMapImages || canReviewSubmissions || canManageMedia || canManageMotw) && (
          <Group heading="Maps & media">
            {canManageMotw && (
              <SidebarLink
                item={{
                  label: 'Map of the Week',
                  path: '/admin/map-of-the-week',
                  icon: CalendarClock,
                }}
                onNavigate={onNavigate}
              />
            )}
            {canManageMapImages && (
              <SidebarLink
                item={{ label: 'Map image library', path: '/admin/map-images', icon: ImageIcon }}
                onNavigate={onNavigate}
              />
            )}
            {canReviewSubmissions && (
              <SidebarLink
                item={{
                  label: 'Map submissions',
                  path: '/admin/map-submissions',
                  icon: Inbox,
                  badge: pendingSubmissions > 0 ? pendingSubmissions : null,
                }}
                onNavigate={onNavigate}
              />
            )}
            {canManageMedia && (
              <SidebarLink
                item={{ label: 'Media library', path: '/admin/media', icon: ImageIcon }}
                onNavigate={onNavigate}
              />
            )}
          </Group>
        )}

        {(canEditContent || canManageNews || canManageServers || canManageStaff) && (
          <Group heading="Site content">
            {canEditContent && (
              <SidebarLink
                item={{ label: 'Content management', path: '/admin/content', icon: FileText }}
                onNavigate={onNavigate}
              />
            )}
            {canManageNews && (
              <SidebarLink
                item={{ label: 'News', path: '/admin/news', icon: Newspaper }}
                onNavigate={onNavigate}
              />
            )}
            {canManageServers && (
              <SidebarLink
                item={{ label: 'Game servers', path: '/admin/servers', icon: Server }}
                onNavigate={onNavigate}
              />
            )}
            {canManageStaff && (
              <SidebarLink
                item={{ label: 'Staff page', path: '/admin/staff', icon: Users }}
                onNavigate={onNavigate}
              />
            )}
            {canEditContent && (
              <SidebarLink
                item={{
                  label: 'Server announcements',
                  path: '/admin/announcements',
                  icon: Megaphone,
                }}
                onNavigate={onNavigate}
              />
            )}
          </Group>
        )}

        {canModerate && (
          <Group heading="Moderation">
            <SidebarLink
              item={{
                label: 'Reported comments',
                path: '/admin/moderation',
                icon: ShieldAlert,
              }}
              onNavigate={onNavigate}
            />
          </Group>
        )}

        {superAdmin && (
          <Group heading="Access">
            <SidebarLink
              item={{
                label: 'Users & permissions',
                path: '/admin/users',
                icon: UserCog,
              }}
              onNavigate={onNavigate}
            />
          </Group>
        )}
      </nav>

      <div className="mx-3 border-t border-border/60 px-2 pb-5 pt-4">
        <Link
          to="/"
          onClick={onNavigate}
          className="mb-1 flex items-center gap-2 rounded-md px-3 py-2 text-xs text-muted-foreground transition-colors hover:bg-secondary/60 hover:text-foreground"
        >
          <ExternalLink className="h-3.5 w-3.5" />
          View public site
        </Link>
        {currentUser && (
          <button
            type="button"
            onClick={async () => {
              if (onNavigate) onNavigate();
              await logout();
            }}
            className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-xs text-muted-foreground transition-colors hover:bg-secondary/60 hover:text-foreground"
          >
            <LogOut className="h-3.5 w-3.5" />
            Log out
          </button>
        )}
      </div>
    </aside>
  );
};

export default AdminSidebar;
